import React, { useRef, useState } from 'react'
import './SelectLocation.scss'
import {
  AutoComplete,
  AutoCompleteChangeParams,
  AutoCompleteCompleteMethodParams,
  AutoCompleteSelectParams,
} from 'primereact/autocomplete'
import { Button } from 'primereact/button'
import { OverlayPanel } from 'primereact/overlaypanel'
import { useTranslation } from 'react-i18next'

// local imports
import ProgressBarFullness from './ProgressBarFullness'
import { getCurrentEmployee } from 'utils/Miscellaneous'
import useDidMountEffect from '../../utils/useDidMountEffect'
import { IAbroad, IFloorList, IFloorPropertiesGroup } from '../../types/FloorType'
import { IMostUsedReservationResponse } from '../../types/Reservation'
import FloorService from '../../service/FloorService'
import { PlanTabs } from '../../types/PlanTabs'
import CountrySelection from './CountrySelection'
import { IEmployeeSummary } from '../../types/Employee'
import AuthUtil from '../../utils/AuthUtil'
import StarRating from './Rating/Rating'
import FilterTab from './location-select/FilterTab'

/* Assets */
import locationGray from './../../assets/icons/location-gray.svg'
import location from './../../assets/icons/location.svg'
import detailIcon from './../../assets/icons/detail-icon.svg'
import filterIcon from './../../assets/icons/filter-icon.svg'
import bookmarkEmpty from 'assets/icons/bookmark-empty.svg'
import bookmarkFilled from 'assets/icons/bookmark-filled.svg'
import doctor from 'assets/icons/doctor.svg'
import gym from 'assets/icons/gym.svg'
import hair from 'assets/icons/hairdresser.svg'
import lactation from 'assets/icons/baby.svg'
import park from 'assets/icons/park-area.svg'
import meetingRoom from 'assets/icons/meeting-room.svg'

export default function SelectLocation(props: {
  placeholder: string
  optionLabel: string
  name: string
  isAbroad: IAbroad
  onLocationChange: (e: AutoCompleteChangeParams) => void
  onLocationSelect: (e: AutoCompleteSelectParams) => void
  onCompleteMethod: (e: AutoCompleteCompleteMethodParams) => void
  onClick: (e: React.MouseEvent<HTMLElement>) => void
  onClear: (e: React.SyntheticEvent) => void
  onShow: () => void
  onHide: () => void
  onFilterHide: () => void
  selectedLocation: IFloorList | IMostUsedReservationResponse | string | undefined
  floorSearchList: any[]
  fullness: boolean
  filterList: (city: [], firm: [], location: [], block: [], floor: [], campus: [], rate: []) => void
  myFirm: boolean
  clearInput: () => void
  setSelectedLocation: any
  setIsAbroad: any
}) {
  const PILOT_FIRMS = !!process.env.REACT_APP_PILOT_FIRMS
    ? process.env.REACT_APP_PILOT_FIRMS.split(',').map((str) => Number(str))
    : []

  const IS_PILOT_USER = !!PILOT_FIRMS?.length ? PILOT_FIRMS.includes(getCurrentEmployee()?.firmId || 0) : true

  const { t } = useTranslation()
  const [display, setDisplay] = useState(false)
  const [allFloorProperties, setAllFloorProperties] = useState<IFloorPropertiesGroup | undefined>(undefined)
  const [optionId, setOptionId] = useState<Number>()

  const employee = AuthUtil.getEmployeeSummary() as IEmployeeSummary
  const selectedItemTemplate = (value: { name: string }, props: { placeholder: string }) => {
    if (value) {
      return (
        <div className="country-item country-item-value d-flex align-items-center">
          <div className="ms-1 me-2">
            <img alt={value.name} src={locationGray} />
          </div>
          <div className="d-flex flex-column selected-title">
            <label>{t('most-used-words.location')}</label>
            <div>{value.name}</div>
          </div>
        </div>
      )
    }

    return (
      <div className="d-flex">
        <img alt="location-gray" src={`../../assets/icons/location-gray.svg`} className="ms-1 me-2" />
        <span>{props.placeholder}</span>
      </div>
    )
  }

  /* Grouped item header Template */
  const groupedItemTemplate = () => {
    if (
      typeof props.floorSearchList !== 'undefined' &&
      props.floorSearchList.length > 0 &&
      props.floorSearchList[0].items.length > 0
    ) {
      return (
        <div className="option-row selected-option-row d-flex justify-content-between">
          <div className="option-column first-column d-flex align-items-center">
            <img alt="head" src={location} className="ms-3 me-3" style={{ visibility: 'hidden' }} />
            <div className="d-flex flex-column option-item flex-wrap">
              <strong style={{ fontSize: '12px', whiteSpace: 'break-spaces' }}>
                {t('most-used-words.reservation-place')}
              </strong>
            </div>
          </div>
          <div className="option-column d-flex align-items-center">
            <div className="d-flex flex-column option-item flex-wrap">
              <strong style={{ fontSize: '12px', whiteSpace: 'break-spaces' }}> {t('most-used-words.block')}</strong>
            </div>
          </div>
          <div className="option-column d-flex align-items-center">
            <div className="d-flex flex-column option-item flex-wrap">
              <strong style={{ fontSize: '12px', whiteSpace: 'break-spaces' }}> {t('most-used-words.floor')}</strong>
            </div>
          </div>

          {props.fullness ? (
            <div className="progress-column d-flex align-items-center px-2">
              <div className="d-flex flex-column option-item flex-wrap">
                <strong style={{ fontSize: '12px', whiteSpace: 'break-spaces' }}> {t('dashboard.occupancy')}</strong>
              </div>
            </div>
          ) : null}

          {/* Campus Services */}
          {!props?.myFirm && (
            <div className="option-column d-flex align-items-center px-2">
              <div className="d-flex flex-column option-item flex-wrap">
                <strong style={{ fontSize: '12px', whiteSpace: 'break-spaces' }}>{t('tabs.on-campus-services')}</strong>
              </div>
            </div>
          )}

          {/* Ratings */}
          {!props?.myFirm && IS_PILOT_USER && AuthUtil.isAdmin(employee?.authList) && (
            <div className="option-column d-flex align-items-center px-1">
              <div className="d-flex flex-column option-item flex-wrap">
                <strong style={{ fontSize: '12px', whiteSpace: 'break-spaces' }}>{t('homepage.point')}</strong>
              </div>
            </div>
          )}

          {/* Favorite */}
          {IS_PILOT_USER && AuthUtil.isAdmin(employee?.authList) && <div className="detail-column" />}

          <div className="detail-column d-flex align-items-center">
            <div className="d-flex flex-column option-item flex-wrap">
              <img alt="filter-icon" onClick={() => setDisplay(true)} src={filterIcon} />
            </div>
          </div>
        </div>
      )
    } else if (
      (props.selectedLocation === '' || props.selectedLocation === undefined) &&
      props.myFirm &&
      props.floorSearchList[0].items.length === 0
    ) {
      return <span>{t('overlaypanel-texts.no-firm-for-office')}</span>
    } else {
      return <span>{t('overlaypanel-texts.no-firm')}</span>
    }
  }

  /* item template */
  const itemTemplate = (option: IFloorList) => {
    const iconSrc = option.isFavorite ? bookmarkFilled : bookmarkEmpty

    return (
      <div
        className="option-row selected-option-row d-flex justify-content-between p-1"
        style={
          option?.id === optionId
            ? { background: 'rgba(13, 202, 240, 0.2)', borderRadius: '15px' }
            : { background: 'transparent' }
        }>
        <div className="option-column first-column d-flex align-items-center">
          <img alt={option?.name} src={location} className="ms-3 me-3 of-location-img" />
          <div className="d-flex flex-column option-item flex-wrap">
            <div className="d-flex flex-wrap">
              <strong style={{ fontSize: '12px', whiteSpace: 'break-spaces' }}>{option?.locationName}</strong>
            </div>
            <div className="d-flex flex-wrap">
              <span style={{ fontSize: '11px', whiteSpace: 'break-spaces' }}>
                {[option?.cityName, option?.districtName, option?.countryName].filter(val => val).join('/')}
              </span>
            </div>
            <div className="d-flex flex-wrap">
              <span style={{ fontSize: '11px', whiteSpace: 'break-spaces' }}>{option?.locationFirmName}</span>
            </div>
            {option?.isReservable && (
              <div className="d-flex flex-wrap">
                <span className="floor-reservation-text">{t('sidebar.table-without-choice')}</span>
              </div>
            )}
          </div>
        </div>
        <div className="option-column d-flex align-items-center">
          <div className="d-flex flex-wrap">
            <label className="" style={{ fontSize: '12px', whiteSpace: 'break-spaces' }}>
              {option?.blockName}
            </label>
          </div>
        </div>
        <div className="option-column d-flex align-items-center">
          <div className="d-flex flex-wrap">
            <label style={{ fontSize: '12px', whiteSpace: 'break-spaces' }}>{option?.name}</label>
          </div>
        </div>
        {props.fullness ? (
          <div className="progress-column d-flex flex-column px-2 justify-content-center">
            <div className="d-flex flex-wrap flex-column">
              <ProgressBarFullness value={option?.occupancy?.percentage} />
              <strong style={{ fontSize: '16px', whiteSpace: 'break-spaces' }}>%{option?.occupancy?.percentage}</strong>
            </div>
          </div>
        ) : null}

        {/* Campus Services */}
        {props.name === PlanTabs.COMMON ? (
          <div className="option-column d-flex align-items-center">
            <div className="d-flex flex-wrap px-2 gap-1">
              {option.hasHealthServiceDoctor && <img alt="health" src={doctor} />}
              {option.hasGYMSaloons && <img alt="gym" src={gym} />}
              {option.hasCoiffeurSaloons && <img alt="coiffeur" src={hair} />}
              {option.hasLactationRoom && <img alt="lactation" src={lactation} />}
              {option.hasParkArea && <img alt="park" src={park} />}
              {option.hasMeetingRoom && <img alt="meeting" src={meetingRoom} />}
            </div>
          </div>
        ) : null}

        {/* Ratings */}
        {props.name === PlanTabs.COMMON && IS_PILOT_USER && AuthUtil.isAdmin(employee?.authList) ? (
          <div className="option-column d-flex px-1 align-items-center">
            <StarRating readOnly value={option?.rate} />
          </div>
        ) : null}

        {/* Favorite */}
        {IS_PILOT_USER && AuthUtil.isAdmin(employee?.authList) && (
          <div className="detail-column d-flex justify-content-center align-items-center">
            <img src={iconSrc} alt="favorite-icon" width={24} height={24} />
          </div>
        )}

        <div className="detail-column d-flex align-items-center">
          <div className="d-flex flex-wrap">
            <img onClick={(e) => floorAllProperties(e, option.id)} alt="detail-icon" src={detailIcon} />
          </div>
        </div>
      </div>
    )
  }

  const opProperties = useRef<OverlayPanel>(null)

  const floorAllProperties = (e: React.MouseEvent<HTMLDivElement>, floorId: number) => {
    setAllFloorProperties(undefined)
    setOptionId(floorId)
    e.stopPropagation()
    FloorService.getAuthAllProperties(floorId).then((response) => {
      opProperties.current?.toggle(e, e.target)
      let _allFloorProperties = response.data.reduce(function (r, a) {
        r[a.type] = r[a.type] || []
        r[a.type].push(a)
        return r
      }, Object.create(null))
      setAllFloorProperties(_allFloorProperties)
    })
  }

  const [selectedCityList, setSelectedCityList] = useState<any>([])
  const [selectedFirmList, setSelectedFirmList] = useState<any>([])
  const [selectedLocationList, setSelectedLocationList] = useState<any>([])
  const [selectedBlockList, setSelectedBlockList] = useState<any>([])
  const [selectedFloorList, setSelectedFloorList] = useState<any>([])
  const [selectedCampServiceList, setSelectedCampServiceList] = useState<any>([])
  const [selectedRateList, setSelectedRateList] = useState<any>([])

  const filterClear = () => {
    setSelectedCityList([])
    setSelectedFirmList([])
    setSelectedLocationList([])
    setSelectedBlockList([])
    setSelectedFloorList([])
    setSelectedCampServiceList([])
    setSelectedRateList([])
    props.filterList([], [], [], [], [], [], [])
  }

  useDidMountEffect(() => {
    props.onFilterHide()
  }, [display])

  const choices = employee.hasAuthAbroadOffice
    ? [
        { name: 'countries.turkey', isAbroad: false },
        { name: 'countries.international-office', isAbroad: true },
      ]
    : [{ name: 'countries.turkey', isAbroad: false }]

  return (
    <div className="dropdown1 row">
      {props.name === PlanTabs.COMMON ? (
        <>
          <div className="col-lg-3 col-12">
            <CountrySelection
              countries={choices}
              isAbroad={props.isAbroad}
              filterClear={filterClear}
              setSelectedLocation={props.setSelectedLocation}
              setIsAbroad={props.setIsAbroad}
            />
          </div>
        </>
      ) : null}

      <div className={props.name === PlanTabs.COMMON ? 'col-lg-9 col-12' : 'col-lg-12 col-12'}>
        <div
          style={{ position: 'relative' }}
          className={typeof props.selectedLocation === 'object' && props.selectedLocation !== null ? 'chips' : ''}>
          <AutoComplete
            value={props.selectedLocation}
            suggestions={props.floorSearchList}
            onChange={props.onLocationChange}
            onSelect={props.onLocationSelect}
            field={props.optionLabel}
            optionGroupLabel={props.optionLabel}
            optionGroupChildren="items"
            className={
              typeof props.selectedLocation === 'object' && props.selectedLocation !== null
                ? 'location-input w-100'
                : 'w-100'
            }
            completeMethod={props.onCompleteMethod}
            placeholder={props.placeholder}
            onClick={
              typeof props.selectedLocation !== 'object' || props.selectedLocation === null ? props.onClick : () => {}
            }
            dropdown
            onClear={props.onClear}
            onShow={props.onShow}
            onHide={props.onHide}
            readOnly={typeof props.selectedLocation === 'object' && props.selectedLocation !== null}
            selectedItemTemplate={selectedItemTemplate}
            itemTemplate={itemTemplate}
            optionGroupTemplate={groupedItemTemplate}
          />
          {typeof props.selectedLocation === 'object' && props.selectedLocation !== null && (
            <Button
              icon="pi pi-times"
              className="p-button-rounded p-button-text p-button-plain"
              style={{ position: 'absolute', right: '4px', top: '8px' }}
              onClick={() => props.clearInput()}
            />
          )}
        </div>
      </div>

      <OverlayPanel ref={opProperties} id="selectLocationOverlayPanel" className="properties-overlaypanel">
        <div className="row">
          <div className="col-4 properties-type">
            <div className="properties-title">{t('overlaypanel-texts.location-properties')}</div>
            {allFloorProperties?.LOCATION && allFloorProperties?.LOCATION.length > 0 ? (
              allFloorProperties?.LOCATION.map((properties) => {
                return <div key={properties.id}>{t(`auth-properties.${properties.name}`)}</div>
              })
            ) : (
              <p className="no-content-text">{t('overlaypanel-texts.location-warning')}</p>
            )}
          </div>
          <div className="col-4 properties-type">
            <div className="properties-title">{t('overlaypanel-texts.block-properties')}</div>
            {allFloorProperties?.BLOCK && allFloorProperties.BLOCK.length > 0 ? (
              allFloorProperties?.BLOCK.map((properties) => {
                return <div key={properties.id}>{t(`auth-properties.${properties.name}`)}</div>
              })
            ) : (
              <p className="no-content-text">{t('overlaypanel-texts.block-warning')}</p>
            )}
          </div>
          <div className="col-4">
            <div className="properties-title">{t('overlaypanel-texts.floor-properties')}</div>
            {allFloorProperties?.FLOOR && allFloorProperties.FLOOR.length > 0 ? (
              allFloorProperties?.FLOOR.map((properties) => {
                return <div key={properties.id}>{t(`auth-properties.${properties.name}`)}</div>
              })
            ) : (
              <p className="no-content-text">{t('overlaypanel-texts.floor-warning')}</p>
            )}
          </div>
        </div>
      </OverlayPanel>

      <FilterTab
        display={display}
        setDisplay={setDisplay}
        floorSearchList={props.floorSearchList}
        name={props.name}
        myFirm={props.myFirm}
        isAbroad={props.isAbroad}
        filterList={props.filterList}
        filterClear={filterClear}
        selectedCityList={selectedCityList}
        setSelectedCityList={setSelectedCityList}
        selectedFirmList={selectedFirmList}
        setSelectedFirmList={setSelectedFirmList}
        selectedLocationList={selectedLocationList}
        setSelectedLocationList={setSelectedLocationList}
        selectedFloorList={selectedFloorList}
        setSelectedFloorList={setSelectedFloorList}
        selectedBlockList={selectedBlockList}
        setSelectedBlockList={setSelectedBlockList}
        selectedCampServiceList={selectedCampServiceList}
        setSelectedCampServiceList={setSelectedCampServiceList}
        selectedRateList={selectedRateList}
        setSelectedRateList={setSelectedRateList}
      />
    </div>
  )
}
